/* eslint-disable react/prop-types */
import { useFooterMenuData } from '@configs/footer/useFooterMenuData';
import { FooterContent, footerData as footerStaticData } from '@configs/footer';
import { FooterTemplate } from '@components/FooterTemplate/FooterTemplate';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { fallbackLng, isSupportedLanguage } from '@configs/i18n';

interface Props {
  language: string;
}

export const AppFooter: React.FC<Props> = ({ language }) => {
  const supportedLang = isSupportedLanguage(language) ? language : fallbackLng;
  const { t: glossaryT } = useTranslation('glossary');

  const { data: footerMenuItems, loading } = useFooterMenuData(supportedLang);

  const baseFooterData: FooterContent = useMemo(() => {
    const { socialLinks, legalLinks, copyright, followUsText } =
      footerStaticData[supportedLang];

    return {
      sections: footerMenuItems.map(item => ({
        key: item.id,
        title: item.title,
        absolute: item.url,
        below:
          item.children?.map(child => ({
            key: child.id,
            title: child.title,
            absolute: child.url,
          })) ?? [],
      })),
      socialLinks,
      legalLinks,
      copyright,
      followUsText,
    };
  }, [footerMenuItems, supportedLang]);

  const accessibilityLink = useMemo(
    () => ({
      href: glossaryT('accessibility.legal.link'),
      title: glossaryT('accessibility.legal.title'),
    }),
    [glossaryT],
  );

  const fullFooterData: FooterContent = useMemo(
    () => ({
      ...baseFooterData,
      legalLinks: [...baseFooterData.legalLinks, accessibilityLink],
    }),
    [baseFooterData, accessibilityLink],
  );

  if (loading) return null;

  return (
    <footer>
      <FooterTemplate
        data={fullFooterData}
        language={supportedLang}
        key={supportedLang}
      />
    </footer>
  );
};
